import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { UsersThree, PaperPlaneTilt, Trash, CaretDown, CaretUp, ArrowClockwise } from "@phosphor-icons/react";

const hrs = (s) => (s / 3600);
const fmtH = (s) => {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return `${h}h ${String(m).padStart(2, "0")}m`;
};
const money = (n) => `$${n.toFixed(2)}`;

export default function TeamPanel() {
  const [members, setMembers] = useState([]);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [form, setForm] = useState({ name: "", email: "", team_role: "content", hourly_rate: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/team"); setMembers(data); }
    catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const invite = async () => {
    setErr(""); setOk(""); setBusy(true);
    try {
      const { data } = await api.post("/admin/team", { ...form, hourly_rate: parseFloat(form.hourly_rate) || 0 });
      if (data.existing_account) {
        setOk(`${form.email} already had an account — Team access was added to it. They sign in as usual and switch to their Team role from the account menu.`);
      } else {
        setOk(data.email_sent ? `Invite sent to ${form.email}.` : `Member added — but the invite email could not be sent. Use "Resend invite".`);
      }
      setForm({ name: "", email: "", team_role: "content", hourly_rate: "" });
      await load();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const patch = async (id, body) => {
    setErr("");
    try { await api.patch(`/admin/team/${id}`, body); await load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div className="card">
      <h2 className="text-xl font-bold font-display flex items-center gap-2 mb-1">
        <UsersThree size={22} weight="duotone" color="#CCFF00" /> Team ({members.filter((m) => m.active).length})
      </h2>
      <p className="text-sm text-[#000000] mb-4">
        Invite people by email and choose what they can change. <b>Content</b> = lesson editor, French strings and French emails.
        <b> All</b> = every admin ability except managing the team. Signed-in work time is tracked automatically so you know what to pay.
      </p>
      <div className="flex items-center gap-2 mb-3 text-sm">
        <button className="btn-ghost text-xs font-semibold border border-[#C4C8CE] rounded-full px-3 py-1.5"
          data-testid="team-weekly-sms-button"
          onClick={async () => {
            try {
              const { data } = await api.post("/admin/team/weekly-summary/send");
              window.alert(data.ok ? `Weekly hours texted to ${data.to}.` : (data.reason || "Could not send."));
            } catch (e) { setErr(formatApiError(e)); }
          }}>
          Text me this week's hours now
        </button>
        <span className="text-xs text-[#555]">Also sent automatically every Sunday at 6pm to your alert phone (Bookings settings).</span>
      </div>
      {err && <div className="text-[#FF5100] text-sm mb-3" data-testid="team-error">{err}</div>}
      {ok && <div className="text-[#1a7f37] text-sm mb-3" data-testid="team-ok">{ok}</div>}

      <div className="p-4 rounded-2xl bg-[#DCDEE2] border border-[#C4C8CE] mb-5">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <input className="input-base" placeholder="Name" value={form.name} data-testid="team-invite-name"
            onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input className="input-base" placeholder="Email" value={form.email} data-testid="team-invite-email"
            onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <select className="input-base" value={form.team_role} data-testid="team-invite-role"
            onChange={(e) => setForm({ ...form, team_role: e.target.value })}>
            <option value="content">Content (lessons & French)</option>
            <option value="all">All (full access)</option>
          </select>
          <input className="input-base" type="number" min="0" step="0.5" placeholder="Hourly pay ($)" value={form.hourly_rate}
            data-testid="team-invite-rate" onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} />
        </div>
        <button className="btn-primary mt-3 flex items-center gap-1.5" onClick={invite} disabled={busy} data-testid="team-invite-button">
          <PaperPlaneTilt size={16} weight="bold" /> {busy ? "Sending…" : "Send invite"}
        </button>
      </div>

      <div className="flex flex-col gap-3">
        {members.map((m) => <MemberRow key={m.id} m={m} onPatch={patch} onErr={setErr} onReload={load} />)}
        {members.length === 0 && <div className="text-sm italic text-[#000000]">No team members yet — invite your first above.</div>}
      </div>
    </div>
  );
}

function MemberRow({ m, onPatch, onErr, onReload }) {
  const [open, setOpen] = useState(false);
  const [rate, setRate] = useState(String(m.hourly_rate || ""));
  const [sessions, setSessions] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payNote, setPayNote] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const rateNum = parseFloat(rate) || 0;

  const loadSessions = async (f = from, t = to) => {
    try {
      const { data } = await api.get(`/admin/team/${m.id}/sessions`, { params: { date_from: f, date_to: t } });
      setSessions(data);
      const p = await api.get(`/admin/team/${m.id}/payouts`);
      setPayouts(p.data);
    } catch (e) { onErr(formatApiError(e)); }
  };
  const toggle = () => { const n = !open; setOpen(n); if (n && !sessions) loadSessions(); };

  const markPaid = async () => {
    try {
      const { data } = await api.post(`/admin/team/${m.id}/payouts`, { note: payNote });
      setPayOpen(false); setPayNote("");
      onErr("");
      window.alert(`Recorded: ${money(data.payout.amount)} paid to ${m.name}. Owed is now $0.00.`);
      onReload();
      if (open) loadSessions();
    } catch (e) { onErr(formatApiError(e)); }
  };

  const resend = async () => {
    try { const { data } = await api.post(`/admin/team/${m.id}/resend-invite`); onErr(""); window.alert(data.email_sent ? "Invite re-sent." : "Email could not be sent — check the address."); }
    catch (e) { onErr(formatApiError(e)); }
  };
  const remove = async () => {
    const msg = m.also_client
      ? `Remove ${m.name}'s TEAM access? Their client account stays untouched, and their logged time is kept.`
      : `Remove ${m.name}'s access? Their logged time is kept.`;
    if (!window.confirm(msg)) return;
    try { await api.delete(`/admin/team/${m.id}`); onReload(); } catch (e) { onErr(formatApiError(e)); }
  };

  const byDate = {};
  (sessions?.sessions || []).forEach((s) => { (byDate[s.date] = byDate[s.date] || []).push(s); });

  return (
    <div className={`rounded-2xl border p-4 ${m.active ? "bg-white border-[#C4C8CE]" : "bg-[#EEE] border-[#DDD] opacity-70"}`}
      data-testid={`team-member-${m.email}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-[160px]">
          <div className="font-bold">{m.name} {!m.active && <span className="text-xs text-[#FF5100] font-semibold">· removed</span>}
            {m.active && !m.accepted && <span className="text-xs text-[#B45309] font-semibold">· invite pending</span>}
            {m.also_client && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[rgba(0,184,212,0.14)] text-[#00768a] ml-1" title={`Also holds: ${(m.other_roles || []).join(", ")}`}>also a client</span>}</div>
          <div className="text-xs text-[#555]">{m.email}</div>
        </div>
        <select className="input-base !w-auto text-sm" value={m.team_role} data-testid={`team-role-${m.email}`}
          onChange={(e) => onPatch(m.id, { team_role: e.target.value })}>
          <option value="content">Content</option>
          <option value="all">All</option>
        </select>
        <label className="flex items-center gap-1 text-sm">
          $<input className="input-base !w-20 text-sm" type="number" min="0" step="0.5" value={rate}
            data-testid={`team-rate-${m.email}`}
            onChange={(e) => setRate(e.target.value)}
            onBlur={() => { if (parseFloat(rate) !== m.hourly_rate) onPatch(m.id, { hourly_rate: parseFloat(rate) || 0 }); }} />/hr
        </label>
        <div className="text-sm ml-auto text-right">
          <div><b>{fmtH(m.total_seconds)}</b> total · {fmtH(m.today_seconds)} today</div>
          <div className="font-bold text-[#1a7f37]" data-testid={`team-owed-${m.email}`}>
            Owed: {money(hrs(m.unpaid_seconds ?? m.total_seconds) * rateNum)}
            {m.paid_seconds > 0 && <span className="text-[10px] font-semibold text-[#555] ml-1">({fmtH(m.paid_seconds)} paid)</span>}
          </div>
        </div>
        <button className="btn-ghost text-xs font-bold border border-[#1a7f37] text-[#1a7f37] rounded-full px-3 py-1.5"
          data-testid={`team-markpaid-${m.email}`} disabled={(m.unpaid_seconds ?? 0) <= 0}
          onClick={() => setPayOpen((v) => !v)}>
          Mark paid
        </button>
        <div className="flex items-center gap-1">
          {m.active && !m.accepted && (
            <button className="btn-ghost p-1.5" title="Resend invite" onClick={resend} data-testid={`team-resend-${m.email}`}>
              <ArrowClockwise size={16} /></button>
          )}
          {m.active ? (
            <button className="btn-ghost p-1.5" title="Remove access" onClick={remove} data-testid={`team-remove-${m.email}`}>
              <Trash size={16} color="#FF5100" /></button>
          ) : (
            <button className="btn-ghost text-xs font-semibold" onClick={() => onPatch(m.id, { active: true })}>Reactivate</button>
          )}
          <button className="btn-ghost p-1.5" onClick={toggle} data-testid={`team-sessions-toggle-${m.email}`}>
            {open ? <CaretUp size={16} /> : <CaretDown size={16} />}</button>
        </div>
      </div>

      {payOpen && (
        <div className="mt-3 pt-3 border-t border-[#E5E7EB] flex flex-wrap items-center gap-2" data-testid={`team-pay-form-${m.email}`}>
          <span className="text-sm">Record <b className="text-[#1a7f37]">{money(hrs(m.unpaid_seconds || 0) * rateNum)}</b> as paid for {fmtH(m.unpaid_seconds || 0)}?</span>
          <input className="input-base text-sm flex-1 min-w-[200px]" placeholder="Payment note — e.g. e-transfer June 15"
            value={payNote} data-testid={`team-pay-note-${m.email}`} onChange={(e) => setPayNote(e.target.value)} />
          <button className="btn-primary text-sm" onClick={markPaid} data-testid={`team-pay-confirm-${m.email}`}>Confirm paid</button>
          <button className="btn-ghost text-sm font-semibold" onClick={() => setPayOpen(false)}>Cancel</button>
        </div>
      )}

      {open && (
        <div className="mt-3 pt-3 border-t border-[#E5E7EB]">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 text-center" data-testid={`team-history-summary-${m.email}`}>
            <div className="rounded-xl bg-[#F3F4F6] p-2">
              <div className="text-[10px] uppercase tracking-wider text-[#555] font-semibold">Total hours (all time)</div>
              <div className="font-bold">{fmtH(m.total_seconds)}</div>
            </div>
            <div className="rounded-xl bg-[#F3F4F6] p-2">
              <div className="text-[10px] uppercase tracking-wider text-[#555] font-semibold">Total paid</div>
              <div className="font-bold">{payouts ? money(payouts.reduce((a, p) => a + (p.amount || 0), 0)) : "…"}
                <span className="text-[10px] font-semibold text-[#555]"> ({fmtH(m.paid_seconds || 0)})</span></div>
            </div>
            <div className="rounded-xl bg-[#F3F4F6] p-2">
              <div className="text-[10px] uppercase tracking-wider text-[#555] font-semibold">Unpaid since last payment</div>
              <div className="font-bold text-[#1a7f37]">{money(hrs(m.unpaid_seconds || 0) * rateNum)}
                <span className="text-[10px] font-semibold text-[#555]"> ({fmtH(m.unpaid_seconds || 0)})</span></div>
            </div>
            <div className="rounded-xl bg-[#F3F4F6] p-2">
              <div className="text-[10px] uppercase tracking-wider text-[#555] font-semibold">Last paid</div>
              <div className="font-bold">{payouts && payouts.length ? new Date(payouts[0].paid_at).toLocaleDateString() : "never"}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 mb-2 flex-wrap text-sm">
            <span className="font-semibold">Sessions</span>
            <input type="date" className="input-base !w-auto text-xs" value={from} onChange={(e) => setFrom(e.target.value)} />
            <span>to</span>
            <input type="date" className="input-base !w-auto text-xs" value={to} onChange={(e) => setTo(e.target.value)} />
            <button className="btn-ghost text-xs font-semibold border border-[#C4C8CE] rounded-full px-3" onClick={() => loadSessions()}>Apply</button>
            <button className="btn-ghost text-xs font-semibold border border-[#C4C8CE] rounded-full px-3" data-testid={`team-alltime-${m.email}`}
              onClick={() => { setFrom(""); setTo(""); loadSessions("", ""); }}>All time</button>
            {sessions && (
              <span className="ml-auto text-xs">
                {from || to ? "Range" : "All time"}: <b>{fmtH(sessions.range_seconds)}</b> · owed <b className="text-[#1a7f37]">{money(hrs(sessions.range_seconds) * rateNum)}</b>
              </span>
            )}
          </div>
          <div className="max-h-64 overflow-y-auto flex flex-col gap-1.5">
            {Object.entries(byDate).map(([d, list]) => (
              <div key={d} className="text-xs">
                <div className="font-bold">{d} · {fmtH(list.reduce((a, s) => a + (s.seconds || 0), 0))}</div>
                {list.map((s) => (
                  <div key={s.id} className="pl-3 text-[#555]">
                    {new Date(s.started_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    {" – "}
                    {s.open ? "now" : new Date(s.ended_at || s.last_beat_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    {" · "}{fmtH(s.seconds || 0)}
                  </div>
                ))}
              </div>
            ))}
            {sessions && !sessions.sessions.length && <div className="text-xs italic text-[#555]">No sessions in this range.</div>}
          </div>
          {payouts && payouts.length > 0 && (
            <div className="mt-3 pt-2 border-t border-[#E5E7EB]">
              <div className="text-sm font-semibold mb-1">Payment history</div>
              <div className="max-h-40 overflow-y-auto flex flex-col gap-1">
                {payouts.map((p) => (
                  <div key={p.id} className="text-xs text-[#555] flex flex-wrap gap-x-2">
                    <span>{new Date(p.paid_at).toLocaleDateString()}</span>
                    <b className="text-[#1a7f37]">{money(p.amount)}</b>
                    <span>({fmtH(p.seconds_covered)} @ ${p.rate}/hr)</span>
                    {p.note && <span className="italic">— {p.note}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
